/**
 * Browser push subscription helpers.
 *
 * Keep this module thin and dependency-free (it's also imported by the
 * settings page, the soft-prompt card, and the SW registration tests).
 */
import { api } from "./api";

const SUBSCRIBE_PATH = "/push/subscribe";
const VAPID_KEY_PATH = "/push/vapid-public-key";

export function isSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getPermission() {
  if (typeof Notification === "undefined") return "denied";
  return Notification.permission;
}

/** Convert a base64url string into the Uint8Array shape pushManager wants. */
function b64urlToUint8Array(b64url) {
  const padding = "=".repeat((4 - (b64url.length % 4)) % 4);
  const base64 = (b64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration() {
  // Wait for the active SW (or fall back to the one we registered ourselves).
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

export async function getCurrentSubscription() {
  if (!isSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Ensure the user is permission-granted and produce a fresh PushSubscription
 * synced to the backend. Throws on permission denial / unsupported / failure.
 */
export async function subscribe() {
  if (!isSupported()) throw new Error("Push notifications are not supported in this browser.");

  let permission = getPermission();
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted.");
  }

  // Fetch the server's VAPID public key.
  const { data } = await api.get(VAPID_KEY_PATH);
  const applicationServerKey = b64urlToUint8Array(data.publicKey);

  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  }
  // POST to backend — upsert by endpoint.
  await api.post(SUBSCRIBE_PATH, sub.toJSON());
  return sub;
}

export async function unsubscribe() {
  if (!isSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch { /* ignore */ }
  try {
    await api.delete(SUBSCRIBE_PATH, { data: { endpoint } });
  } catch (e) {
    // 404 means the server already cleaned it — still a success from the
    // caller's perspective.
    if (e?.response?.status !== 404) throw e;
  }
  return true;
}
